// Tooltip com portal — aparece imediatamente no hover (vs title HTML que demora ~1s).
// Usa position: fixed + createPortal pra não ser cortado por overflow:hidden/auto de pais
// (ex: tabelas, cards, sidebar). Mobile: alterna ao tocar; fecha ao tocar fora.
//
// Renderiza SEM wrapper extra: clona o filho e anexa ref + handlers nele.
// Isso preserva layouts grid/flex (Bootstrap row/col-auto, etc.) que dependem do filho ser
// descendente direto do container.
//
// Adaptado de https://github.com/lucasxf11/flor-de-cafe (que usava wrapper span).
import React, { useState, useRef, useEffect, cloneElement, Children } from 'react';
import { createPortal } from 'react-dom';

export default function Tooltip({ texto, children, largura = 260 }) {
  const [visivel, setVisivel] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, posicao: 'top' });
  const ref = useRef(null);

  if (!texto) return children;

  function atualizarPos() {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const margem = 8;
    const altEstimadaTooltip = 60;
    const cabeAcima = rect.top > altEstimadaTooltip + margem;
    const top = cabeAcima ? rect.top - margem : rect.bottom + margem;
    const left = rect.left + rect.width / 2;
    setCoords({ top, left, posicao: cabeAcima ? 'top' : 'bottom' });
  }

  function mostrar() { atualizarPos(); setVisivel(true); }
  function esconder() { setVisivel(false); }
  function alternar(e) {
    if (e && e.stopPropagation) e.stopPropagation();
    setVisivel(v => { if (!v) atualizarPos(); return !v; });
  }

  useEffect(() => {
    if (!visivel) return;
    function onScroll() { atualizarPos(); }
    function onClickFora() { esconder(); }
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    document.addEventListener('click', onClickFora);
    document.addEventListener('touchstart', onClickFora);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
      document.removeEventListener('click', onClickFora);
      document.removeEventListener('touchstart', onClickFora);
    };
  }, [visivel]);

  // Encadeia handlers existentes do filho com os do tooltip
  const child = Children.only(children);
  const childHandlers = child.props || {};
  const chain = (orig, ours) => (e) => {
    if (orig) orig(e);
    ours(e);
  };

  const enhanced = cloneElement(child, {
    ref,
    onMouseEnter: chain(childHandlers.onMouseEnter, mostrar),
    onMouseLeave: chain(childHandlers.onMouseLeave, esconder),
    onFocus: chain(childHandlers.onFocus, mostrar),
    onBlur: chain(childHandlers.onBlur, esconder),
    onClick: chain(childHandlers.onClick, alternar),
  });

  return (
    <>
      {enhanced}
      {visivel && createPortal(
        <div
          role="tooltip"
          style={{
            position: 'fixed',
            top: coords.top,
            left: coords.left,
            transform: coords.posicao === 'top' ? 'translate(-50%, -100%)' : 'translate(-50%, 0)',
            background: '#3F2A38',
            color: '#fff',
            fontSize: '0.82rem',
            lineHeight: 1.4,
            padding: '8px 12px',
            borderRadius: 6,
            width: largura,
            maxWidth: 'calc(100vw - 24px)',
            whiteSpace: 'normal',
            textAlign: 'center',
            zIndex: 9999,
            pointerEvents: 'none',
            boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
          }}
        >
          {texto}
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              [coords.posicao === 'top' ? 'bottom' : 'top']: -5,
              left: '50%',
              transform: `translateX(-50%) ${coords.posicao === 'top' ? '' : 'rotate(180deg)'}`,
              width: 0,
              height: 0,
              borderLeft: '5px solid transparent',
              borderRight: '5px solid transparent',
              borderTop: '5px solid #3F2A38',
            }}
          />
        </div>,
        document.body
      )}
    </>
  );
}
